import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { isKambiSharedCoupon } from '@core/Areas/BetLoop/helpers/typeGuards/isKambiSharedCoupon'
import { getKambiSportsBetTypeForAnalytics } from '@core/Areas/KambiSports/helpers/analytics'
import type { ShareBetData, BetLoopShare } from '@core/Areas/ShareBet/types'

export const trackShareBetLinkPlaced = (data: ShareBetLinkPlacedData) => {
  _trackShareBetEvent({
    interaction_type: 'Bet Placed',
    betType: data.betType,
    spend: String(data.spend),
    betStatus: 'Pending',
    raceCode: data.raceCode,
    sportCode: data.sportCode,
    share_origin: data.share_origin,
    event_value: data.event_value ? String(data.event_value) : undefined,
    has_special: data.has_special,
    legs: String(data.legs),
  })
}

export const trackShareBetLinkOpen = ({ share_origin, betType }: ShareBetLinkOpenData) => {
  _trackShareBetEvent({ interaction_type: 'Link Opened', share_origin, betType })
}

export const trackShareBetShared = (data: ShareBetSharedData) => {
  const {
    location,
    betType,
    spend,
    payout,
    betStatus,
    costShared,
    odds,
    destination,
    raceCode,
    sportCode,
  } = data
  _trackShareBetEvent({
    interaction_type: 'Bet Shared',
    location,
    betType,
    spend: spend ? String(spend) : undefined,
    payout: payout ? String(payout) : undefined,
    betStatus,
    costShared,
    odds: odds ? String(odds) : undefined,
    destination,
    raceCode,
    sportCode,
  })
}

export const trackShareBetAcquisition = ({
  share_origin,
}: Pick<ShareBetLinkPlacedData, 'share_origin'>) => {
  _trackShareBetEvent({ interaction_type: 'Acquisition', share_origin })
}

export const trackShareBetLogin = ({
  share_origin,
}: Pick<ShareBetLinkPlacedData, 'share_origin'>) => {
  _trackShareBetEvent({ interaction_type: 'Login', share_origin })
}

export const trackShareBetError = ({
  destination,
  message,
  location,
  betType,
}: ShareBetErrorData) => {
  _trackShareBetEvent({ interaction_type: 'Share Error', destination, message, location, betType })
}

export const trackShareBetToBetLoopError = ({
  message,
  location,
  betType,
  raceCode,
  destination = 'Bet Loop',
}: ShareBetToBetLoopErrorData) => {
  _trackShareBetEvent({
    interaction_type: 'Share Error',
    destination,
    message,
    location,
    betType,
    raceCode,
  })
}

export function extractShareContext(data: ShareBetData | Exclude<ShareBetData, BetLoopShare>): {
  location: string
  betType?: string
} {
  if (data.contextType === 'ticket') {
    return {
      location: data.share.sourceLocation ?? 'transacted-bets',
      betType: data.share.ticket.productName,
    }
  }

  if (data.contextType === 'betslip') {
    return {
      location: 'bet-slip',
      betType: data.share.items.length > 1 ? 'multi' : 'single',
    }
  }

  if (data.contextType === 'quickbet') {
    return {
      location: 'quickbet',
      betType: data.share.items[0]?.bettingType,
    }
  }

  if (data.contextType === 'betloop') {
    return {
      location: 'bet-loop',
      betType: isKambiSharedCoupon(data.share.ticket)
        ? getKambiSportsBetTypeForAnalytics(data.share.ticket)
        : 'productName' in data.share.ticket
          ? data.share.ticket.productName
          : 'unknown',
    }
  }

  if (data.contextType === 'kambi') {
    return {
      location: 'quickbet',
      betType: getKambiSportsBetTypeForAnalytics(data.share.coupon),
    }
  }

  return { location: 'unknown' }
}

// =============
// Local Helpers
// =============

const defaultAnalyticsData: Record<keyof ShareBetDataCombined, undefined> = {
  betStatus: undefined,
  betType: undefined,
  costShared: undefined,
  destination: undefined,
  event_value: undefined,
  has_special: undefined,
  legs: undefined,
  location: undefined,
  message: undefined,
  odds: undefined,
  payout: undefined,
  raceCode: undefined,
  share_origin: undefined,
  spend: undefined,
  sportCode: undefined,
}

function _trackShareBetEvent(
  data: {
    interaction_type: string
  } & Record<string, unknown>
) {
  trackEvent(keys.shareBet, { ...defaultAnalyticsData, ...data })
}

// =====
// Types
// =====

type ShareBetDataCombined = ShareBetLinkPlacedData &
  ShareBetErrorData &
  ShareBetLinkOpenData &
  ShareBetSharedData &
  ShareBetToBetLoopErrorData

type ShareBetLinkPlacedData = {
  has_special: boolean
  legs: number
  raceCode: string | undefined
  share_origin: string
  spend: number
  betType?: string
  event_value?: number
  /** Sport code for Kambi sports bets (e.g., 'football', 'basketball') */
  sportCode?: string
}

type ShareBetErrorData = {
  destination: ShareDestination
  location: string
  message: string
  betType?: string
}

type ShareBetToBetLoopErrorData = {
  destination: ShareDestination
  location: string | undefined
  raceCode: string | undefined
  message: string | undefined
  betType?: string
}

type ShareBetLinkOpenData = { betType: string; share_origin: string }

type ShareBetSharedData = {
  betStatus: string
  costShared: boolean
  location: string
  betType?: string
  destination?: ShareDestination
  odds?: number | null
  payout?: number | null
  raceCode?: string
  spend?: number | null
  /** Sport code for Kambi sports bets (e.g., 'football', 'basketball') */
  sportCode?: string
}

type ShareDestination = 'Bet Loop' | 'External'
